import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Star, TrendingUp } from 'lucide-react';

const ChronoSanctuaryBlog = () => {
  const [selectedDate, setSelectedDate] = useState<string>('2025-08-08');
  const [horoscopeData, setHoroscopeData] = useState<any>(null);
  const [animatedNumbers, setAnimatedNumbers] = useState({
    days: 0,
    watches: 0,
    predictions: 0,
    uptime: 0
  });

  // Dates disponibles (5 juin au 8 août 2025)
  const availableDates: string[] = [];
  const startDate = new Date('2025-06-05');
  const endDate = new Date('2025-08-08');
  
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    availableDates.push(d.toISOString().split('T')[0]);
  }

  // Animation des chiffres au chargement
  useEffect(() => {
    const targets = { days: 58, watches: 696, predictions: 20000, uptime: 100 };
    const duration = 2000;
    const increment = 50;
    
    const timers: NodeJS.Timeout[] = [];
    
    Object.keys(targets).forEach((key) => {
      const target = targets[key as keyof typeof targets];
      const steps = duration / increment;
      const stepValue = target / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += stepValue;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setAnimatedNumbers(prev => ({
          ...prev,
          [key]: Math.floor(current)
        }));
      }, increment);
      
      timers.push(timer);
    });
    
    return () => timers.forEach(timer => clearInterval(timer));
  }, []);

  // Charger les données
  React.useEffect(() => {
    fetch(`/archives/astrodaily/${selectedDate}/today.json`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setHoroscopeData(data))
      .catch(() => setHoroscopeData(null));
  }, [selectedDate]);

  const navigateDate = (direction: number) => {
    const currentIndex = availableDates.indexOf(selectedDate);
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < availableDates.length) {
      setSelectedDate(availableDates[newIndex]);
    }
  };

  const zodiacSigns = [
    { id: 'belier', name: 'Bélier', symbol: '♈', color: '#FF6B6B' },
    { id: 'taureau', name: 'Taureau', symbol: '♉', color: '#8B7355' },
    { id: 'gemeaux', name: 'Gémeaux', symbol: '♊', color: '#87CEEB' },
    { id: 'cancer', name: 'Cancer', symbol: '♋', color: '#4682B4' },
    { id: 'lion', name: 'Lion', symbol: '♌', color: '#FFA500' },
    { id: 'vierge', name: 'Vierge', symbol: '♍', color: '#90EE90' },
    { id: 'balance', name: 'Balance', symbol: '♎', color: '#FFB6C1' },
    { id: 'scorpion', name: 'Scorpion', symbol: '♏', color: '#8B0000' },
    { id: 'sagittaire', name: 'Sagittaire', symbol: '♐', color: '#9370DB' },
    { id: 'capricorne', name: 'Capricorne', symbol: '♑', color: '#696969' },
    { id: 'verseau', name: 'Verseau', symbol: '♒', color: '#00CED1' },
    { id: 'poissons', name: 'Poissons', symbol: '♓', color: '#20B2AA' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #000000, #0a0a0b)',
      color: '#fafafa',
      overflowX: 'hidden'
    }}>
      {/* Header élégant avec meilleur cadrage */}
      <div style={{ 
        background: 'linear-gradient(135deg, #000000, #1a1a1a)',
        padding: '4rem 2rem 3rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Effet de fond animé */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255, 107, 107, 0.1) 0%, transparent 50%)',
          animation: 'pulse 10s ease-in-out infinite'
        }} />
        
        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '300',
            marginBottom: '1rem',
            letterSpacing: '0.05em',
            background: 'linear-gradient(90deg, #FFD700, #FFA500, #FF6347)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            ChronoSanctuary
          </h1>
          
          <p style={{ 
            fontSize: 'clamp(1rem, 2vw, 1.3rem)', 
            color: '#888', 
            marginBottom: '3rem',
            letterSpacing: '0.02em'
          }}>
            Archives AstroDaily — L'héritage de 58 jours de perfection cosmique
          </p>

          {/* Stats animées avec défilement horizontal */}
          <div style={{
            display: 'flex',
            gap: '2rem',
            overflowX: 'auto',
            paddingBottom: '1rem',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#333 transparent'
          }}>
            <div style={{
              minWidth: '200px',
              textAlign: 'center',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.05), rgba(255, 215, 0, 0.01))',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: '200', 
                color: '#FFD700',
                fontFamily: 'monospace'
              }}>
                {animatedNumbers.days}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Jours de Service
              </div>
            </div>

            <div style={{
              minWidth: '200px',
              textAlign: 'center',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.05), rgba(255, 165, 0, 0.01))',
              border: '1px solid rgba(255, 165, 0, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: '200', 
                color: '#FFA500',
                fontFamily: 'monospace'
              }}>
                {animatedNumbers.watches}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Cadrans Créés
              </div>
            </div>

            <div style={{
              minWidth: '200px',
              textAlign: 'center',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(255, 99, 71, 0.05), rgba(255, 99, 71, 0.01))',
              border: '1px solid rgba(255, 99, 71, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: '200', 
                color: '#FF6347',
                fontFamily: 'monospace'
              }}>
                {animatedNumbers.predictions.toLocaleString()}+
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Prédictions
              </div>
            </div>

            <div style={{
              minWidth: '200px',
              textAlign: 'center',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(50, 205, 50, 0.05), rgba(50, 205, 50, 0.01))',
              border: '1px solid rgba(50, 205, 50, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: '200', 
                color: '#32CD32',
                fontFamily: 'monospace'
              }}>
                {animatedNumbers.uptime}%
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Uptime Garanti
              </div>
            </div>
          </div>

          {/* Message sur la perfection - design épuré */}
          <div style={{
            marginTop: '3rem',
            padding: '2rem',
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '16px'
          }}>
            <p style={{ 
              lineHeight: '1.8', 
              color: '#aaa',
              fontSize: '1.05rem'
            }}>
              <span style={{ color: '#FFD700', fontWeight: '500' }}>Système Finalisé :</span> Après 58 jours d'opération parfaite, 
              AstroDaily reste la preuve qu'un système peut atteindre l'excellence millimétrique. 
              Chaque montre générée, chaque prédiction calculée avec les vraies données astronomiques, 
              créant <span style={{ color: '#FFA500' }}>696 œuvres uniques</span> et 
              <span style={{ color: '#FF6347' }}> 20'000+ horoscopes cosmiques</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div style={{ padding: '3rem 2rem' }}>
        {/* Navigation par date - design minimaliste */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto 3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '2rem',
          flexWrap: 'wrap',
          padding: '1.5rem',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <button
            onClick={() => navigateDate(-1)}
            disabled={availableDates.indexOf(selectedDate) === 0}
            style={{
              padding: '0.75rem 1.5rem',
              background: availableDates.indexOf(selectedDate) === 0 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: availableDates.indexOf(selectedDate) === 0 ? '#444' : '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: availableDates.indexOf(selectedDate) === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.95rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
          >
            <ChevronLeft size={18} />
            Précédent
          </button>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            flex: '1',
            justifyContent: 'center'
          }}>
            <Calendar size={22} color="#FFD700" />
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                padding: '0.6rem 1rem',
                background: '#0a0a0b',
                color: '#fafafa',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                fontSize: '1rem',
                minWidth: '250px',
                cursor: 'pointer'
              }}
            >
              {availableDates.map(date => (
                <option key={date} value={date}>
                  {new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => navigateDate(1)}
            disabled={availableDates.indexOf(selectedDate) === availableDates.length - 1}
            style={{
              padding: '0.75rem 1.5rem',
              background: availableDates.indexOf(selectedDate) === availableDates.length - 1 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: availableDates.indexOf(selectedDate) === availableDates.length - 1 ? '#444' : '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: availableDates.indexOf(selectedDate) === availableDates.length - 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.95rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
          >
            Suivant
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Grille des montres */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {zodiacSigns.map((sign, index) => {
            const horoscope = horoscopeData?.horoscopes?.[index];
            return (
              <div
                key={sign.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: `1px solid rgba(255, 255, 255, 0.05)`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 10px 30px ${sign.color}22`;
                  e.currentTarget.style.borderColor = `${sign.color}44`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                {/* Image du cadran */}
                <div style={{
                  width: '100%',
                  aspectRatio: '1',
                  background: '#0a0a0b',
                  position: 'relative'
                }}>
                  <img
                    src={`/archives/astrodaily/${selectedDate}/${sign.id}.png`}
                    alt={`${sign.name} - ${selectedDate}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      if (target.parentElement) {
                        target.parentElement.innerHTML = `
                          <div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 5rem; color: ${sign.color}44">
                            ${sign.symbol}
                          </div>
                        `;
                      }
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(0, 0, 0, 0.8)',
                    backdropFilter: 'blur(10px)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '1.3rem'
                  }}>
                    {sign.symbol}
                  </div>
                </div>

                {/* Informations */}
                <div style={{ padding: '1.25rem' }}>
                  <h3 style={{
                    fontSize: '1.3rem',
                    fontWeight: '400',
                    color: sign.color,
                    marginBottom: '0.75rem',
                    letterSpacing: '0.02em'
                  }}>
                    {sign.name}
                  </h3>

                  {horoscope ? (
                    <>
                      <p style={{
                        fontSize: '0.95rem',
                        color: '#888',
                        lineHeight: '1.6',
                        marginBottom: '1rem'
                      }}>
                        {horoscope.fr?.texte_court_fr || 'Prédiction archivée'}
                      </p>

                      {/* Énergies avec design moderne */}
                      {horoscope.energy_stats && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem', 
                          flexWrap: 'wrap',
                          marginBottom: '0.75rem'
                        }}>
                          {Object.entries(horoscope.energy_stats).map(([key, value]: [string, any]) => (
                            <div
                              key={key}
                              style={{
                                padding: '0.3rem 0.6rem',
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.3rem'
                              }}
                            >
                              <span>{key === 'amour' ? '❤️' : key === 'chance' ? '🍀' : key === 'travail' ? '💼' : '⭐'}</span>
                              <span style={{ color: '#FFD700' }}>{value}%</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Numéros chance */}
                      {horoscope.lucky_numbers && (
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#FFA500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <Star size={14} />
                          <span>Chance : {horoscope.lucky_numbers.join(' • ')}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ fontSize: '0.9rem', color: '#555' }}>
                      Archive du {selectedDate}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer épuré */}
        <div style={{
          textAlign: 'center',
          marginTop: '5rem',
          padding: '3rem 2rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <p style={{ 
            fontSize: '1.1rem',
            color: '#666',
            fontStyle: 'italic',
            marginBottom: '0.5rem'
          }}>
            "Les étoiles se souviennent de tout."
          </p>
          <p style={{ fontSize: '0.9rem', color: '#444' }}>
            ChronoSanctuary × AstroDaily Archives
          </p>
        </div>
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        ::-webkit-scrollbar {
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.02);
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default ChronoSanctuaryBlog;